import { useState, useEffect } from 'react'
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SyncProgressProps {
  isVisible: boolean
  onComplete?: () => void
}

interface SyncStatus {
  isLoading: boolean
  progress: number
  currentStep: string
  totalSteps: number
  currentStepIndex: number
  error: string | null
  success: boolean
}

export const SyncProgress = ({ isVisible, onComplete }: SyncProgressProps) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isLoading: false,
    progress: 0,
    currentStep: '',
    totalSteps: 0,
    currentStepIndex: 0,
    error: null,
    success: false
  })

  useEffect(() => {
    if (!window.electronAPI || !isVisible) return

    // Listen for sync progress updates
    window.electronAPI.on('sync-progress', (data) => {
      setSyncStatus(data)
      
      if (data.success && onComplete) {
        setTimeout(() => {
          onComplete()
        }, 2000) // Show success for 2 seconds before closing
      }
    })

    return () => {
      window.electronAPI.removeAllListeners('sync-progress')
    }
  }, [isVisible, onComplete])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 shadow-xl">
        <div className="text-center">
          {/* Header */}
          <div className="mb-6">
            {syncStatus.success ? (
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            ) : syncStatus.error ? (
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            ) : (
              <div className="relative">
                <RefreshCw className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
              </div>
            )}
            
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {syncStatus.success ? 'Sync Complete!' : 
               syncStatus.error ? 'Sync Failed' : 
               'Syncing Data...'}
            </h2>
          </div>

          {/* Progress */}
          {!syncStatus.success && !syncStatus.error && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>
                  {syncStatus.currentStepIndex > 0 ? `${syncStatus.currentStepIndex}/${syncStatus.totalSteps}` : 'Initializing...'}
                </span>
                <span>{Math.round(syncStatus.progress)}%</span>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${syncStatus.progress}%` }}
                />
              </div>

              <p className="text-sm text-gray-700 dark:text-gray-300">
                {syncStatus.currentStep || 'Preparing sync...'}
              </p>
            </div>
          )}

          {/* Success message */}
          {syncStatus.success && (
            <div className="mb-6">
              <p className="text-green-600 dark:text-green-400 mb-2">
                ✅ Successfully synced all data
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your POS system is ready to use
              </p>
            </div>
          )}

          {/* Error message */}
          {syncStatus.error && (
            <div className="mb-6">
              <p className="text-red-600 dark:text-red-400 mb-2">
                Failed to sync data
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 bg-red-50 dark:bg-red-900/20 p-3 rounded">
                {syncStatus.error}
              </p>
              <button
                onClick={() => {
                  setSyncStatus(prev => ({ ...prev, error: null, isLoading: true, progress: 0 }))
                  // Trigger retry
                  if (window.electronAPI?.sync?.syncFromCloud) {
                    window.electronAPI.sync.syncFromCloud()
                  }
                }}
                className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors"
              >
                <RefreshCw className="h-4 w-4 inline mr-2" />
                Retry Sync
              </button>
            </div>
          )}

          {/* Loading indicator for steps */}
          {syncStatus.isLoading && !syncStatus.error && (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Processing...
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}